import { Injectable, signal } from '@angular/core';
import {
  BankAccount,
  BankAccountService as ApiBankAccountService,
  MerchantService,
  StripeService,
} from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { CreateBankAccountRequest } from './add-bank-account/add-bank-account.component';
import { ScriptLoaderService } from '../payment-method/stripe.service';

// https://docs.stripe.com/js
declare let Stripe: any;

@Injectable({
  providedIn: 'root',
})
export class BankAccountService {
  // https://docs.stripe.com/js
  private stripe: any;
  private stripeKey = '';

  private bankAccounts$$ = new ReplaySubject<BankAccount[]>(1);
  bankAccounts$ = this.bankAccounts$$.asObservable();

  savingBankAccount = signal<boolean>(false);
  addBankAccountError = signal<string | null>(null);

  bankAccountProcessing = signal<{ [key: string]: boolean }>({});

  constructor(
    private bankAccountService: ApiBankAccountService,
    private stripeService: StripeService,
    private stripeScriptService: ScriptLoaderService,
    private merchantService: MerchantService,
    private snackbarService: SnackbarService,
  ) {
    this.stripeKey = this.stripeService.getConnectPublicKey();
    this.stripeScriptService
      .load()
      .pipe(first())
      .subscribe(() => {
        this.stripe = Stripe(this.stripeKey);
      });
  }

  public async load(merchantId: string): Promise<void> {
    let bankAccounts: BankAccount[];
    try {
      bankAccounts = await firstValueFrom(this.bankAccountService.listBankAccounts(merchantId));
    } catch (err) {
      const e = err as HttpErrorResponse;
      if (e.status === 412) {
        // Expected - merchant may not have Vendasta payments setup yet
        bankAccounts = [];
      }
    } finally {
      this.bankAccounts$$.next(bankAccounts);
    }
  }

  async setDefault(merchantId: string, id: string): Promise<void> {
    try {
      await firstValueFrom(this.bankAccountService.setDefaultBankAccount(merchantId, id));
    } catch (err) {
      let errorMessage: string;
      if (err?.error?.message) {
        errorMessage = err.error.message;
      } else {
        errorMessage = err.error;
      }
      this.snackbarService.openErrorSnack(errorMessage);
    } finally {
      await this.load(merchantId);
    }
  }

  async delete(merchantId: string, id: string): Promise<void> {
    try {
      await firstValueFrom(this.bankAccountService.deleteBankAccount(merchantId, id));
    } catch (err) {
      let errorMessage: string;
      if (err?.error?.message) {
        errorMessage = err.error.message;
      } else {
        errorMessage = err.error;
      }
      this.snackbarService.openErrorSnack(errorMessage);
    } finally {
      await this.load(merchantId);
    }
  }

  async create(merchantId: string, request: CreateBankAccountRequest, skipSnack?: boolean): Promise<true | void> {
    try {
      this.savingBankAccount.set(true);
      const result = await this.stripe.createToken('bank_account', request);
      if (result.error) {
        let errorMessage = '';
        if (result.error.message) {
          errorMessage = result.error.message;
        } else {
          errorMessage = result.error;
        }
        this.addBankAccountError.set(errorMessage);
      }
      await firstValueFrom(this.merchantService.createExternalAccount(merchantId, result.token.id));
      if (!skipSnack) {
        this.snackbarService.openSuccessSnack('BANK_ACCOUNT.ADD.SUCCESS_SNACK');
      }
      this.load(merchantId);
      this.addBankAccountError.set(null);
      return true;
    } catch (err) {
      if (!skipSnack) {
        this.snackbarService.openErrorSnack('BANK_ACCOUNT.ADD.ERROR_SNACK');
      }
    } finally {
      this.savingBankAccount.set(false);
    }
  }
}
